/**
 * SubmitItems.js
 *
 * A player's screen during the submission step (after the last item, before the results).
 * Teams that bought more items than they submit pick which ones count:
 * - Their best set (rules first, then score with bonuses) is selected to start with
 * - The score, bonuses and any rules missed update as they change the selection
 * - Anyone on the team can submit, and change it until the host shows the results
 * Teams with nothing to choose just see what they're submitting.
 */

import React, { useState } from "react";
import { Check } from "lucide-react";
import toast from "react-hot-toast";
import { getAuctionSettings } from "../assets/Rules";
import { getTeamColor } from "../assets/teamColors";
import { getRoomLibrary } from "../library/libraries";
import { saveSubmission } from "../helpers/auctionLobby";
import { scoreItems, scoreTeam } from "../helpers/scoring";

const SubmitItems = ({ room, team }) => {
    const settings = getAuctionSettings(room);
    const library = getRoomLibrary(room);
    const scored = scoreTeam(team, settings, library);
    const { items } = scored;
    const size = Math.min(settings.itemsToSubmit, items.length);
    const hasChoice = items.length > size;

    // Starts on the team's saved choice, or its best set
    const [selected, setSelected] = useState(() => new Set(scored.submittedIndexes));
    const [isSaving, setIsSaving] = useState(false);

    const submittedNow = Array.isArray(team.submission) && scored.chosenByTeam;
    const savedSet = new Set(submittedNow ? team.submission : []);
    const isUnchanged = submittedNow && savedSet.size === selected.size && [...selected].every((index) => savedSet.has(index));

    const preview = scoreItems([...selected].map((index) => items[index]), settings, library);
    const otherAttributes = library.attributes.filter((attribute) => attribute.kind !== "name" && attribute.kind !== "score");
    const color = getTeamColor(team);

    const toggle = (index) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else if (next.size < size) next.add(index);
            else toast.error(`You can only submit ${size}. Untick one first.`);
            return next;
        });
    };

    const handleSubmit = async () => {
        setIsSaving(true);
        const saved = await saveSubmission(room.id, team.id, [...selected].sort((a, b) => a - b));
        setIsSaving(false);
        if (saved) toast.success("Submitted. You can still change it until the host shows the results.");
        else toast.error("Couldn't submit. Try again.");
    };

    return (
        <div className="w-full max-w-3xl mx-auto px-5 pt-24 pb-16 text-zinc-100 text-left">
            <div className={`rounded-2xl px-6 py-5 ${color.bg} ${color.text === "text-white" ? "text-white" : "text-black"}`}>
                <div className="text-sm uppercase tracking-widest opacity-80">Submission</div>
                <div className="text-3xl font-extrabold">{team.name}</div>
            </div>

            {hasChoice ? (
                <p className="mt-6 text-lg text-zinc-300">
                    You bought {items.length} items. Pick the <span className="text-white font-semibold">{size}</span> you want to submit:
                    only these count. Your best set is selected to start with.
                </p>
            ) : (
                <p className="mt-6 text-lg text-zinc-300">
                    You bought {items.length} item{items.length === 1 ? "" : "s"}, so all of them are submitted.
                    Waiting for the other teams and the host.
                </p>
            )}

            {/* --- Live score of the selection --- */}
            <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-950 p-5">
                <div className="flex items-baseline justify-between gap-4">
                    <span className="text-zinc-400">{selected.size} / {size} selected</span>
                    <span className="text-4xl font-extrabold text-yellow">{preview.total}</span>
                </div>
                <div className="mt-1 text-right text-zinc-400">
                    {preview.baseScore} {library.scoreAttribute}{preview.bonus > 0 && ` + ${preview.bonus} bonus`}
                </div>
                {preview.bonuses.map((bonus) => <div key={bonus} className="text-green">{bonus}</div>)}
                {selected.size === size && preview.problems.map((problem) => <div key={problem} className="text-coral">{problem}</div>)}
            </div>

            {/* --- Items --- */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {items.map((item, index) => {
                    const isSelected = selected.has(index);
                    return (
                        <button
                            key={index}
                            type="button"
                            disabled={!hasChoice}
                            onClick={() => toggle(index)}
                            aria-pressed={isSelected}
                            className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition-colors ${
                                isSelected ? "border-yellow bg-yellow/10" : "border-zinc-800 bg-zinc-950 text-zinc-500"
                            } ${hasChoice ? "hover:border-zinc-500" : ""}`}
                        >
                            <span className={`mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border ${isSelected ? "border-yellow bg-yellow text-black" : "border-zinc-600"}`}>
                                {isSelected && <Check size={16} strokeWidth={3} />}
                            </span>
                            <span>
                                <span className={`block text-lg font-semibold ${isSelected ? "text-white" : ""}`}>{item.name}</span>
                                <span className="block text-sm">
                                    {[...otherAttributes.map((attribute) => item.values?.[attribute.name]).filter(Boolean), `${library.scoreAttribute} ${item.score}`].join(" · ")}
                                </span>
                            </span>
                        </button>
                    );
                })}
            </div>

            {hasChoice && (
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSaving || selected.size !== size || isUnchanged}
                    className="mt-8 w-full rounded-2xl bg-yellow py-4 text-xl font-extrabold text-black transition-opacity disabled:opacity-40"
                >
                    {isSaving ? "Submitting..." : isUnchanged ? "Submitted ✓" : submittedNow ? "Update submission" : `Submit these ${size}`}
                </button>
            )}
        </div>
    );
};

export default SubmitItems;
